import { Alert, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconSettings } from '@tabler/icons-react';
import { WidgetDefinition, WidgetProps, WidgetType } from '../types';
import { AreaChart, BarChart, LineChart, PieChart, DonutChart, ScatterChart } from '@mantine/charts';
import { parseDates } from '../utils';
import { CriticalStockTable } from './CriticalStockTable';
import { shapeCriticalStockRows } from './criticalStockData';
import { TableWidget } from './TableWidget';
import { capTableRows, computeTotals, flattenTableColumns, selectVisibleColumns } from './tableData';
import {
  isCompareWidgetData, isTableWidgetData, isWidgetDataError, isWidgetDataIncomplete, type WidgetDataState
} from '../widgetData';
import { KpiWidget } from './KpiWidget';
import { isKpiThresholds, shapeKpiData, sumMeasure } from './kpiData';
import { readKpiPartialCoverageMeasure, resolveKpiState } from './kpiState';
import { TextWidget } from './TextWidget';
import { HeadingWidget } from './HeadingWidget';
import { SpacerWidget } from './SpacerWidget';
import { CountWidget } from './CountWidget';
import { AlertWidget } from './AlertWidget';
import { parseColorSteps } from './colorSteps';
import { ListWidget } from './ListWidget';
import { ProgressWidget } from './ProgressWidget';
import { resolveReadmodelWidgetState } from './readmodelState';
import { isAlertSeverity } from '../readmodelSource';
import { isCustomTint } from '../../WidgetCard/tintTokens';
import { componentsTestIds } from '@components/testids';

const widgetComponents: Partial<Record<WidgetType, any>> = {
    areaChart: AreaChart,
    barChart: BarChart,
    lineChart: LineChart,
    pieChart: PieChart,
    donutChart: DonutChart,
    scatterChart: ScatterChart,
    criticalStock: CriticalStockTable,
    table: TableWidget,
    kpi: KpiWidget,
    text: TextWidget,
    // TV1-2291 (t21) — the two static structure widgets. Both DATALESS: they are
    // members of `DATALESS_WIDGET_TYPES`, so `getWidgetData` short-circuits to the
    // `DATALESS_WIDGET_DATA` sentinel and no Cube query is ever built for them.
    heading: HeadingWidget,
    spacer: SpacerWidget,
    // TV1-2291 (t25) — the four READMODEL-backed widgets. Members of
    // `READMODEL_WIDGET_TYPES`, so `getWidgetData` takes its THIRD branch for them
    // (`fetchReadmodelWidgetData`) and they never reach Cube. What arrives here is a
    // `ReadmodelWidgetData` (`{ rows, count }`), not a `ResultSet` projection.
    count: CountWidget,
    alert: AlertWidget,
    list: ListWidget,
    progress: ProgressWidget
};

/**
 * TV1-2291 (t21) — render-time facts a props builder may need that are neither the
 * fetched data nor the widget's own props.
 *
 * There is exactly one today (`editMode`), and it exists because `spacer` renders
 * DIFFERENTLY for a reader than for an author: nothing at all versus a subtle
 * placeholder. That distinction cannot come from the definition — it is a property
 * of WHO is looking — and it must not be spread onto the component blindly either:
 * `<WidgetComponent {...widgetProps} editMode={…} />` would hand Mantine's charts an
 * unknown DOM prop on every render. Routing it through the builders means only the
 * widgets that asked for it receive it.
 */
export type WidgetRenderContext = {
    editMode: boolean;
    /**
     * The readmodel collection the card is bound to, when it has one (2026-08-19).
     *
     * ⚠️ It lives at the DEFINITION ROOT (`ReadmodelQuery.collection`), NOT in
     * `widgetComponentProps` — the props bag holds presentation, the root holds the
     * query. A builder that reached for `widgetComponentProps.collection` would read
     * `undefined` on every real card and only "work" in a fixture that happened to
     * put it there.
     *
     * It is here rather than passed as a fourth argument because it is exactly what
     * this type is for: a render-time fact that is neither the fetched data nor the
     * widget's own props. Its one consumer today is `alert`, which picks its badge
     * icon by subject.
     */
    collection?: string;
};

const parseAreaChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.series && widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const seriesField = widgetComponentProps.series;
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        const groupedData = data.reduce(( acc: any, item: any ) => {
            const xValue = item[xAxisField];
            const seriesValue = item[seriesField];
            const yValue = parseFloat( item[yAxisField]) || 0; 
            
            if ( !acc[xValue]) {
                acc[xValue] = { [xAxisField]: xValue };
            }
            
            acc[xValue][seriesValue] = yValue;
            
            return acc;
        }, {});
        
        return parseDates( Object.values( groupedData ), widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parseBarChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.series && widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const seriesField = widgetComponentProps.series;
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        const groupedData = data.reduce(( acc: any, item: any ) => {
            const xValue = item[xAxisField];
            const seriesValue = item[seriesField];
            const yValue = parseFloat( item[yAxisField]) || 0; 
            
            if ( !acc[xValue]) {
                acc[xValue] = { [xAxisField]: xValue };
            }
            
            acc[xValue][seriesValue] = yValue;
            
            return acc;
        }, {});
        
       return parseDates( Object.values( groupedData ), widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};
const parseLineChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.series && widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const seriesField = widgetComponentProps.series;
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        const groupedData = data.reduce(( acc: any, item: any ) => {
            const xValue = item[xAxisField];
            const seriesValue = item[seriesField];
            const yValue = parseFloat( item[yAxisField]) || 0; 
            
            if ( !acc[xValue]) {
                acc[xValue] = { [xAxisField]: xValue };
            }
            
            acc[xValue][seriesValue] = yValue;
            
            return acc;
        }, {});
        
        return parseDates( Object.values( groupedData ), widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parsePieChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    if ( widgetComponentProps?.groupBy && widgetComponentProps?.valueMetric ) {
        const groupByField = widgetComponentProps.groupBy;
        const metricField = widgetComponentProps.valueMetric;
        return data.map(( item, index ) => ({
            name: item[groupByField],
            value: parseFloat( item[metricField]) || 0,
            color: seriesColorAt( index, widgetComponentProps )
        }));
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parseDonutChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    return parsePieChartData( data, widgetComponentProps, widgetDefinition );
};

const parseScatterChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
  if ( !Array.isArray( data )) return data;

  const { dataKey, groupBy } = widgetComponentProps ?? {};
  if (
    !dataKey ||
    typeof dataKey !== 'object' ||
    typeof dataKey.x !== 'string' ||
    typeof dataKey.y !== 'string' ||
    typeof groupBy !== 'string'
  ) {
    return data;
  }

  const xField = dataKey.x;
  const yField = dataKey.y;

  const grouped: Record<string, { name: string; data: any[] }> = {};

  data.forEach(( row: any ) => {
    const groupValue = row[groupBy];
    const xValue = row[xField];
    const yValue = row[yField];

    if ( groupValue == null || xValue == null || yValue == null ) return;

    if ( !grouped[groupValue]) {
      grouped[groupValue] = {
        name: groupValue,
        data: []
      };
    }

    grouped[groupValue].data.push({ [xField]: xValue, [yField]: yValue });
  });
  return Object.values( grouped ).map(( series, index ) => ({
    ...series,
    color: seriesColorAt( index, widgetComponentProps )
  }));
};

// TV1-1933 (S18) — shape raw CriticalStock cube rows into a units-below-
// threshold-sorted list.
//
// TV1-2392 (slice 6) — no more `threshold` prop, no more client-side cut. The
// predicate (`CriticalStock.belowThreshold`) is entirely server-computed;
// `shapeCriticalStockRows` only SELECTS by it and returns the partial-coverage
// signal alongside the rows (see that module's header).
const parseCriticalStockData = ( data: any, widgetComponentProps: any ) =>
    shapeCriticalStockRows( data, {
        skuField: widgetComponentProps?.skuField,
        warehouseIdField: widgetComponentProps?.warehouseIdField,
        currentStockField: widgetComponentProps?.currentStockField,
        dailyVelocityField: widgetComponentProps?.dailyVelocityField,
        thresholdUnitsField: widgetComponentProps?.thresholdUnitsField,
        belowThresholdField: widgetComponentProps?.belowThresholdField,
        unitsBelowThresholdField: widgetComponentProps?.unitsBelowThresholdField
    });

// TV1-1929 (S14) — the `table` widget does NOT receive `rawData()`: `getWidgetData`
// hands it `{ rows, columns }` off `tablePivot()`/`tableColumns()` (see
// `Widgets/widgetData.ts`). All this parser does is the display-side shaping that
// must NOT happen inside the fetch: flatten the (possibly pivoted, nested) column
// tree to leaves, apply the user's column selection/order, and cap the rows so an
// unbounded query cannot blow up the DOM.
const parseTableData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !isTableWidgetData( data )) return { rows: [], columns: [], totals: null, truncated: false };

    const rows = capTableRows( data.rows, widgetDefinition?.limit );
    const columns = selectVisibleColumns(
        flattenTableColumns( data.columns ),
        widgetComponentProps?.visibleColumns
    );
    // Totals are summed over the RENDERED rows (see `computeTotals` — Cube's
    // `totalRow()` is not an aggregate). `truncated` lets the footer say so
    // instead of passing a partial sum off as the grand total.
    const truncated = data.rows.length > rows.length;

    return {
        rows,
        columns,
        totals: widgetComponentProps?.showTotals ? computeTotals( rows, columns ) : null,
        truncated
    };
};

// TV1-1930 (S15) — the KPI takes EITHER a flat `rawData()` array (no compare) or
// the `{ current, prior }` pair that `extractWidgetData` produces from
// `decompose()` when `dateRange.compare` is on. `shapeKpiData` tells the two
// apart by shape and totals the single measure over each period. `measures[0]` is
// the DISPLAYED measure (a witness, when declared, rides at `measures[1]`).
//
// TV1-2291 (t15) — and, alongside the number, WHY there is none. `resolveKpiState`
// is the only thing that can answer that, and this parser is the only hook in the
// pipeline handed BOTH the raw rows (where the witness measure lives) and the
// widget definition (where `activityMeasure` / `prerequisite` are declared) — the
// props builder downstream sees neither. So the state is resolved here and
// threaded through as a prop; `KpiWidget` renders a verdict, it does not derive
// one.
//
// ⚠️ The `error` state is UNREACHABLE from here by construction: `WebWidget`
// returns `WidgetErrorTile` for a failed fetch BEFORE any parser runs (and
// `MobileWidget` does the same). That generic tile is deliberately left as the
// app's KPI error rendering — it already names the Cube member it could not
// resolve and is consistent across every widget type. `resolveKpiState` still
// accepts the envelope, so a caller that resolves its own state gets the fourth
// treatment; this seam simply never produces it.
// TV1-2392 (slice 6) — the partial-coverage WITNESS: how many rows behind this KPI
// resolved no umbral at all (`CriticalStock.unresolvedThresholdCount`). The measure
// is named EXPLICITLY by the tile, as `widgetComponentProps.partialCoverageMeasure`
// (`readKpiPartialCoverageMeasure`) — never taken from the positional
// `definition.measures[1]`, which in this repo is the generic ACTIVITY-witness slot
// that `kpi-net-revenue` / `kpi-net-margin` / `kpi-avg-ticket` / `kpi-return-rate`
// already fill with `Sales.orderCount` / `ReturnRate.soldUnits`. A positive activity
// witness is the HEALTHY case and a positive coverage witness is the ALARM case, so
// reading one slot for both meanings printed the stock-umbral sentence under every
// healthy revenue number. Opt-in by name, not by position.
//
// This is also deliberately NOT the
// `activityMeasure` mechanism `kpiState.ts` already has: that one only fires
// while the DISPLAYED value is itself unmeasured (`kpi.value === null`), and is
// silently ignored once a value exists ("if the number arrived, the
// prerequisite is evidently satisfied"). The gate this ticket adds is the
// opposite case — the KPI shows a confident small number (SOME rows resolved)
// while OTHER rows never did — so it must surface independently of `state`,
// not through it.
export const readPartialCoverage = ( data: any, witnessMeasure: string | null | undefined ): boolean => {
    if ( !witnessMeasure ) return false;
    const rows = isCompareWidgetData( data ) ? data.current : ( Array.isArray( data ) ? data : null );
    const witness = sumMeasure( rows, witnessMeasure );
    return witness !== null && witness > 0;
};

const parseKpiData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    const measure = widgetDefinition?.measures?.[0] ?? '';

    return {
        ...shapeKpiData( data, measure ),
        state: resolveKpiState({ data, measure, widgetComponentProps }),
        partialCoverage: readPartialCoverage( data, readKpiPartialCoverageMeasure( widgetComponentProps ))
    };
};

// TV1-2291 (t21) — `heading` has no data to parse; what it needs is its own
// `title`, and the PARSER is the only hook in this pipeline that is handed the
// widget DEFINITION. So it is used as the carrier: parser reads the title off the
// definition, props builder puts it in the component's prop. That keeps the
// "section title IS `UiWidgetBaseSchema.title`" decision (no separate content
// field, in the schema or here) working without widening the props-builder
// signature a second time.
const parseHeadingData = ( _data: any, _widgetComponentProps: any, widgetDefinition: WidgetDefinition ): string =>
    widgetDefinition?.title ?? '';

// TV1-2291 (t25) — the READMODEL parser, shared by all four types.
//
// One parser, because the four tiles differ in how they DRAW `{ rows, count }`, not
// in how they read it. `resolveReadmodelWidgetState` turns the payload into the
// discriminated state (`loading` / `error` / `unavailable` / `empty` / `ready`) that
// every one of them switches on; the per-type props builders below add only display
// configuration on top.
//
// ⚠️ It does NOT fall through to `count: 0` for a payload of the wrong shape — that
// is the `unavailable` member, and it exists precisely so a persisted card whose
// type was switched from `barChart` to `count` cannot print a confident zero derived
// from a Cube result set. See `readmodelState.ts`.
const parseReadmodelData = ( data: any ) => resolveReadmodelWidgetState( data );

const widgetParsers:Partial<Record<WidgetType, ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => any>> = {
    areaChart: parseAreaChartData,
    barChart: parseBarChartData,
    lineChart: parseLineChartData,
    pieChart: parsePieChartData,
    donutChart: parseDonutChartData,
    scatterChart: parseScatterChartData,
    criticalStock: parseCriticalStockData,
    table: parseTableData,
    kpi: parseKpiData,
    heading: parseHeadingData,
    // No `spacer` entry: it has neither data nor a definition field to read.
    count: parseReadmodelData,
    alert: parseReadmodelData,
    list: parseReadmodelData,
    progress: parseReadmodelData
};

// Mantine colors for series
const mantineColors = [
    'blue.6', 'indigo.6', 'violet.6', 'grape.6', 'pink.6', 'red.6', 
    'orange.6', 'yellow.6', 'lime.6', 'green.6', 'teal.6', 'cyan.6'
];

/**
 * The colour for series `index` — the author's, if they picked one (2026-08-17).
 *
 * `widgetComponentProps.seriesColors` is an ARRAY, positional, and may be shorter
 * than the number of series: entry `i` overrides series `i`, and anything past the
 * end falls back to the built-in cycle. That is deliberate — a chart's series count
 * comes from the DATA and changes as the data does, so a scheme that demanded one
 * colour per series would break the moment a new channel appeared. Picking the
 * first two and letting the rest ride the default is the common case.
 *
 * Validated with the SAME `isCustomTint` guard as the card tint, for the same
 * reason: the value reaches the DOM on a shared document, so `#rrggbb` and nothing
 * else. An invalid entry falls through to the default rather than being dropped —
 * a chart with a missing series colour is worse than one with a default one.
 *
 * Kept as `getRandomColor`'s replacement rather than a wrapper so there is one
 * definition of "what colour is series i", used by all three call sites (pie/donut
 * slices, scatter groups, and the cartesian `resolveChartSeries`).
 */
const seriesColorAt = ( index: number, widgetComponentProps?: any ): string => {
    const chosen = widgetComponentProps?.seriesColors?.[index];
    if ( isCustomTint( chosen )) return chosen;
    return mantineColors[index % mantineColors.length];
};

/**
 * The `series` array Mantine's cartesian charts REQUIRE (TV1-1939).
 *
 * ## Two defects, one helper
 *
 * **1. `series` was `undefined` whenever `widgetComponentProps.series` was absent —
 * and Mantine THROWS.** `BarChart` / `LineChart` / `AreaChart` all declare
 * `series: Series[]` as a REQUIRED prop and map over it. The old code only built the
 * array `if ( mergedProps.series && … )`, i.e. only when the author had already set
 * a `series` prop, so any chart without one took the whole report down at render.
 * That is not a hypothetical: the LLM's closed props schema could not express
 * `series` at all until TV1-1939, so *every* generated chart crashed.
 *
 * Deriving the series from the DATA — always, whether or not the author set anything
 * — makes an unrenderable chart structurally impossible. There is no longer a code
 * path that hands Mantine an `undefined` series.
 *
 * **2. Non-numeric columns were plotted as series.** The old filter kept every column
 * except `xAxis`. On a query with a `granularity`, Cube returns BOTH the raw dimension
 * (`Sales.orderDate`) AND the granular key (`Sales.orderDate.day`), so charting
 * against the granular key left the RAW DATE STRING as a "series" — a line rendered
 * from text. Same for any dimension column on a non-pivoted query.
 *
 * Keeping only columns whose value is NUMERIC fixes both cases at once, and needs no
 * knowledge of which branch produced the rows: after the pivot the series columns hold
 * numbers, and on the flat path the measures do. Dimension and date columns are
 * strings and drop out by construction.
 *
 * `xAxisField` is still excluded explicitly — a time axis bucketed to a UNIX epoch, or
 * any numeric dimension used as an x axis, would otherwise pass the numeric test and
 * plot itself against itself.
 */
export const resolveChartSeries = (
    data: unknown,
    xAxisField: string | undefined,
    // Optional so the many existing call sites and tests that only care about
    // WHICH series are found keep working; absent, every series takes the default
    // palette exactly as before.
    widgetComponentProps?: any
): Array<{ name: string; color: string }> => {
    if ( !Array.isArray( data ) || data.length === 0 ) return [];

    const firstItem = data[0];
    if ( typeof firstItem !== 'object' || firstItem === null ) return [];

    const seriesKeys = Object.keys( firstItem ).filter(( key ) => {
        if ( key === xAxisField ) return false;
        const value = ( firstItem as Record<string, unknown> )[key];
        // Cube returns measures as numbers, but a raw-SQL measure can arrive as a
        // numeric STRING ("1234.50"). Both are plottable; a date or a channel name is
        // not. `Number('')` is 0, so the empty string is excluded explicitly.
        if ( typeof value === 'number' ) return Number.isFinite( value );
        return typeof value === 'string' && value.trim() !== '' && Number.isFinite( Number( value ));
    });

    return seriesKeys.map(( key, index ) => ({
        name: key,
        color: seriesColorAt( index, widgetComponentProps )
    }));
};

// Domain widget-config fields that are consumed UPSTREAM by the data parsers
// (to shape the series / set `dataKey`) — they are NOT Mantine chart props.
// Forwarding them into the chart render props makes Mantine spread them onto its
// root <div>, which trips React "unknown DOM prop" warnings (xAxis/yAxis on
// line/bar/area, groupBy/valueMetric on pie/donut).
const CHART_CONFIG_ONLY_PROPS = [ 'xAxis', 'yAxis', 'groupBy', 'valueMetric', 'series' ];

const stripConfigOnlyProps = ( props: any ) =>
    Object.fromEntries( Object.entries( props ?? {}).filter(([key]) => !CHART_CONFIG_ONLY_PROPS.includes( key )));

const widgetPropsBuilders: Partial<Record<WidgetType, ( data: any, widgetComponentProps: any, context: WidgetRenderContext ) => any>> = {
    areaChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            curveType: 'linear',
            tickLine: 'xy',
            withGradient: true,
            withXAxis: true,
            withYAxis: true,
            gridAxis: 'x',
            withDots: true,
            connectNulls: true,
            withLegend: true
        };
        
        const mergedProps:any = { ...defaultProps };
        mergedProps.dataKey = widgetComponentProps.dataKey || widgetComponentProps.xAxis;

        if ( widgetComponentProps ) {
            Object.keys( widgetComponentProps ).forEach( key => {
                // Config-only fields were consumed by the parser upstream; never
                // forward them to Mantine (they leak onto the DOM otherwise).
                if ( CHART_CONFIG_ONLY_PROPS.includes( key )) return;
                if ( widgetComponentProps[key] !== null ) {
                    if( !Array.isArray( widgetComponentProps[key]) || ( Array.isArray( widgetComponentProps[key]) && widgetComponentProps[key].length )){
                        mergedProps[key] = widgetComponentProps[key];
                    }
                }
            });
        }
        
        // ALWAYS an array — Mantine requires `series` and throws on undefined. See
        // `resolveChartSeries`.
        mergedProps.series = resolveChartSeries( data, mergedProps.xAxis || widgetComponentProps?.xAxis, widgetComponentProps );
        
        return { data, h:'100%', ...mergedProps };
    },
    barChart: ( data: any, widgetComponentProps: any ) =>{
        const defaultProps = {
            tickLine: 'xy',
            withXAxis: true,
            withYAxis: true,
            gridAxis: 'x',
            orientation: 'horizontal',
            withLegend: true
        };
        
        const mergedProps:any = { ...defaultProps };
        mergedProps.dataKey = widgetComponentProps.dataKey || widgetComponentProps.xAxis;

        if ( widgetComponentProps ) {
            Object.keys( widgetComponentProps ).forEach( key => {
                // Config-only fields were consumed by the parser upstream; never
                // forward them to Mantine (they leak onto the DOM otherwise).
                if ( CHART_CONFIG_ONLY_PROPS.includes( key )) return;
                if ( widgetComponentProps[key] !== null ) {
                    if( !Array.isArray( widgetComponentProps[key]) || ( Array.isArray( widgetComponentProps[key]) && widgetComponentProps[key].length )){
                        mergedProps[key] = widgetComponentProps[key];
                    }
                }
            });
        }
        
        // ALWAYS an array — Mantine requires `series` and throws on undefined. See
        // `resolveChartSeries`.
        mergedProps.series = resolveChartSeries( data, mergedProps.xAxis || widgetComponentProps?.xAxis, widgetComponentProps );
        
        return { data, h:'100%', ...mergedProps };
    },
    lineChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            curveType: 'linear',
            tickLine: 'xy',
            withXAxis: true,
            withYAxis: true,
            gridAxis: 'x',
            withDots: true,
            connectNulls: true,
            withLegend: true
        };
        
        const mergedProps:any = { ...defaultProps };
        mergedProps.dataKey = widgetComponentProps.dataKey || widgetComponentProps.xAxis;

        if ( widgetComponentProps ) {
            Object.keys( widgetComponentProps ).forEach( key => {
                // Config-only fields were consumed by the parser upstream; never
                // forward them to Mantine (they leak onto the DOM otherwise).
                if ( CHART_CONFIG_ONLY_PROPS.includes( key )) return;
                if ( widgetComponentProps[key] !== null ) {
                    if( !Array.isArray( widgetComponentProps[key]) || ( Array.isArray( widgetComponentProps[key]) && widgetComponentProps[key].length )){
                        mergedProps[key] = widgetComponentProps[key];
                    }
                }
            });
        }
        
        // ALWAYS an array — Mantine requires `series` and throws on undefined. See
        // `resolveChartSeries`.
        mergedProps.series = resolveChartSeries( data, mergedProps.xAxis || widgetComponentProps?.xAxis, widgetComponentProps );
        
        return { data, h:'100%', ...mergedProps };
    },
    pieChart: ( data: any, widgetComponentProps: any ) => {
        // `withLegend` is NOT a Mantine v8 PieChart prop — omitted so it doesn't
        // leak onto the DOM. `labelsPosition` IS valid on PieChart, so it stays.
        const defaultProps = {
            withLabelsLine: false,
            labelsPosition: 'outside',
            labelsType: 'value',
            withLabels: true
        };
        
        const mergedProps:any = { ...defaultProps };
        
        if ( widgetComponentProps ) {
            Object.keys( widgetComponentProps ).forEach( key => {
                // Config-only fields were consumed by the parser upstream; never
                // forward them to Mantine (they leak onto the DOM otherwise).
                if ( CHART_CONFIG_ONLY_PROPS.includes( key )) return;
                if ( widgetComponentProps[key] !== null ) {
                    if( !Array.isArray( widgetComponentProps[key]) || ( Array.isArray( widgetComponentProps[key]) && widgetComponentProps[key].length )){
                        mergedProps[key] = widgetComponentProps[key];
                    }
                }
            });
        }
        
        return { data, h:'100%', size: '100%', ...mergedProps };
    },
    donutChart: ( data: any, widgetComponentProps: any ) => {
        // Mantine v8 DonutChart has neither `withLegend` nor `labelsPosition`
        // (only `labelsType`) — both omitted so they don't leak onto the DOM.
        const defaultProps = {
            withLabelsLine: false,
            labelsType: 'value',
            withLabels: true
        };
        
        const mergedProps:any = { ...defaultProps };
        
        if ( widgetComponentProps ) {
            Object.keys( widgetComponentProps ).forEach( key => {
                // Config-only fields were consumed by the parser upstream; never
                // forward them to Mantine (they leak onto the DOM otherwise).
                if ( CHART_CONFIG_ONLY_PROPS.includes( key )) return;
                if ( widgetComponentProps[key] !== null ) {
                    if( !Array.isArray( widgetComponentProps[key]) || ( Array.isArray( widgetComponentProps[key]) && widgetComponentProps[key].length )){
                        mergedProps[key] = widgetComponentProps[key];
                    }
                }
            });
        }
        
        return { data, h:'100%', size: '100%', ...mergedProps };
    },
    scatterChart: ( parsedData: any, widgetComponentProps: any ) => {
    const { dataKey = {}} = widgetComponentProps ?? {};

    const defaultProps = {
      legendProps: { verticalAlign: 'bottom', height: 20 }
    };

    return {
      ...defaultProps,
      ...stripConfigOnlyProps( widgetComponentProps ),
      data: parsedData,
      h: '100%',
      dataKey,
      xAxisLabel: dataKey.x,
      yAxisLabel: dataKey.y
    };
  },
    // CriticalStockTable takes the shaped rows + optional column selection, plus
    // (TV1-2392 slice 6) the partial-coverage disclosure computed by
    // `shapeCriticalStockRows` from the SAME unfiltered fetch that produced the
    // rows — never a second query.
    criticalStock: ( parsed: any, widgetComponentProps: any ) => ({
        rows: parsed?.rows ?? [],
        partialCoverage: Boolean( parsed?.partialCoverage ),
        columns: widgetComponentProps?.columns
    }),
    // TableWidget is handed the parser output verbatim — `parseTableData` already
    // did the flattening, the column selection, the row cap and the totals.
    // `pivotConfig` is NOT forwarded: it was consumed upstream, at `tablePivot()`
    // time, which is exactly why pivot needs no special case in the renderer.
    table: ( parsed: any ) => ({
        rows: parsed?.rows ?? [],
        columns: parsed?.columns ?? [],
        totals: parsed?.totals ?? null,
        truncated: Boolean( parsed?.truncated )
    }),
    // KPI: the parser already produced `{ value, delta, hasCompare }`; this only
    // threads the DISPLAY props through. Note `goodDirection` is NOT defaulted
    // here — `KpiWidget` defaults it to `'neutral'` (no colour). Defaulting it to
    // `'up'` anywhere would paint a rising return-rate tile confidently green.
    kpi: ( parsed: any, widgetComponentProps: any, context: WidgetRenderContext ) => ({
        // 🔗 EDIT MODE SUPPRESSES THE LINK — see `KpiWidget`. A card being arranged
        // must not navigate on click; that is the reader's affordance, not the
        // author's.
        editMode: context.editMode,
        value: parsed?.value ?? null,
        textValue: parsed?.textValue ?? null,
        delta: parsed?.delta ?? { kind: 'unknown' },
        hasCompare: Boolean( parsed?.hasCompare ),
        // TV1-2291 (t15) — WHY the tile looks the way it does, resolved by the
        // parser (the only hook with the raw rows AND the definition). Left
        // undefined only if a caller bypassed the parser; `KpiWidget` then derives
        // the verdict that claims least rather than falling back to an em-dash.
        state: parsed?.state,
        // TV1-2392 (slice 6) — SOME rows resolved an umbral while others did not.
        // Independent of `state`: it must show even when the value is a confident
        // small number (see `readPartialCoverage`).
        partialCoverage: Boolean( parsed?.partialCoverage ),
        format: widgetComponentProps?.format,
        currency: widgetComponentProps?.currency,
        decimals: widgetComponentProps?.decimals,
        goodDirection: widgetComponentProps?.goodDirection,
        caption: widgetComponentProps?.caption,
        // PREFACTOR-grammar — value-band colour, string values, and a link target.
        // All three ride on `widgetComponentProps`, which every widget schema
        // declares as `S.shape({}).additionalProperties( true )` — so they are FREE
        // at the domain layer: no `anyOf` change, no regeneration, and none of the
        // 17-site tax a new WidgetType costs. These are kpi PROPS, not new types.
        //
        // `thresholds` is validated rather than spread: a malformed one from an
        // LLM-authored definition would otherwise silently paint the tile.
        thresholds: isKpiThresholds( widgetComponentProps?.thresholds )
            ? widgetComponentProps.thresholds
            : undefined,
        valueMode: widgetComponentProps?.valueMode === 'text' ? 'text' : 'number',
        href: typeof widgetComponentProps?.href === 'string' && widgetComponentProps.href
            ? widgetComponentProps.href
            : undefined
    }),
    // TV1-1930 — `text` is DATALESS: `data` is the `DATALESS_WIDGET_DATA` sentinel
    // (truthy, so it clears WebWidget's `if (!data) return <Skeleton/>` guard) and
    // is deliberately ignored. The content lives entirely in the widget props.
    text: ( _parsed: any, widgetComponentProps: any ) => ({
        content: widgetComponentProps?.content
    }),
    // TV1-2291 (t21) — `heading` is DATALESS too: `parsed` here is the widget's own
    // `title`, lifted off the definition by `parseHeadingData`. `widgetComponentProps`
    // is ignored on purpose — the schema declares no props for a heading, and reading
    // a `content`-like key out of the permissive bag would quietly invent the second
    // source of truth the design exists to avoid.
    heading: ( parsed: any ) => ({
        title: typeof parsed === 'string' ? parsed : undefined
    }),
    // `spacer` takes NOTHING from the definition — only who is looking. See
    // `WidgetRenderContext`: view mode draws an empty box, edit mode a placeholder.
    spacer: ( _parsed: any, _widgetComponentProps: any, context: WidgetRenderContext ) => ({
        editMode: context.editMode
    }),
    // TV1-2291 (t25) — the four READMODEL tiles. `parsed` is the state
    // `parseReadmodelData` resolved; everything else here is display configuration
    // off `widgetComponentProps`, which every widget schema declares as
    // `additionalProperties( true )`.
    //
    // ⚠️ The QUERY (`collection` / `filter` / `limit`) is deliberately absent from
    // all four: it lives at the top level of the definition, it was consumed by
    // `getWidgetData` before this ran, and a renderer that could see it would invite
    // a second, divergent read of the binding.
    count: ( parsed: any, widgetComponentProps: any, context: WidgetRenderContext ) => ({
        state: parsed,
        editMode: context.editMode,
        // 🔴 Passed through RAW and allow-listed inside `CountWidget` by
        // `sanitizeKpiHref` — one sanitiser, at the boundary where a string becomes a
        // navigation. Sanitising here as well would create a second place to forget.
        href: typeof widgetComponentProps?.href === 'string' && widgetComponentProps.href
            ? widgetComponentProps.href
            : undefined,
        caption: widgetComponentProps?.caption
    }),
    // 🔴 `href` and `severity` are read HERE, from the props bag, exactly as
    // `UiWidgetAlertSchema` declares them — never from the definition root. Ticket
    // 23's reasoning: `widgetGrammar.modelVariant()` omits only `id` and
    // `widgetComponentProps`, so a top-level `href` would be inherited into the
    // schema handed to a language model the day `alert` joins `MODEL_WIDGET_VARIANTS`.
    //
    // `href` is passed through RAW and allow-listed inside `AlertWidget` by
    // `sanitizeKpiHref` — one sanitiser, at the boundary where the string becomes a
    // navigation. Sanitising here as well would create a second place to forget.
    alert: ( parsed: any, widgetComponentProps: any, context: WidgetRenderContext ) => ({
        state: parsed,
        // 🔗 EDIT MODE SUPPRESSES THE LINK — see `AlertWidget`.
        editMode: context.editMode,
        // `isAlertSeverity` rather than a spread: a persisted `severity: 'catastrophic'`
        // (or an absent one) must fall back to the middle claim, not index a class
        // table with `undefined` and render an untreated box.
        severity: isAlertSeverity( widgetComponentProps?.severity )
            ? widgetComponentProps.severity
            : undefined,
        href: typeof widgetComponentProps?.href === 'string' && widgetComponentProps.href
            ? widgetComponentProps.href
            : undefined,
        // STEP COLOURS (2026-08-18). Parsed here rather than in the renderer so the
        // untrusted array is validated once, on the way in — `parseColorSteps` drops
        // malformed entries and rejects any colour that is not an anchored hex, which
        // is the same boundary `isCustomTint` guards for the card tint.
        colorSteps: parseColorSteps( widgetComponentProps?.colorSteps ),
        caption: widgetComponentProps?.caption
    }),
    list: ( parsed: any, widgetComponentProps: any ) => ({
        state: parsed,
        // Passed through unvalidated on purpose: `deriveListColumns` is the one place
        // that decides what a usable column list is, and it also owns the DERIVED
        // fallback for a card whose author has not configured one yet.
        columns: widgetComponentProps?.columns
    }),
    progress: ( parsed: any, widgetComponentProps: any ) => ({
        state: parsed,
        completedField: widgetComponentProps?.completedField,
        totalField: widgetComponentProps?.totalField,
        caption: widgetComponentProps?.caption
    })
};

/**
 * PREFACTOR-grammar — the error tile.
 *
 * "loading", "no data" and "broken" must be three visibly different things. They
 * were two: a failed Cube query returned `null`, never entered the data map, and
 * rendered as the same `<Skeleton>` a pending fetch does — so a widget pointing at
 * a measure that does not exist just spun, forever, silently.
 *
 * The message is shown, not just logged: a Cube error names the member it could
 * not resolve, which is exactly what the person looking at the tile needs.
 */
const WidgetErrorTile = ({ error }: { error: string }) => (
    <Alert
        variant="light"
        color="red"
        h="100%"
        icon={<IconAlertTriangle size={18} />}
        title={<span data-testid={componentsTestIds.widgetErrorTitle}>Widget failed to load</span>}
        data-testid={componentsTestIds.widgetError}
        styles={{ root: { overflow: 'auto' }}}
    >
        <Text size="xs" data-testid={componentsTestIds.widgetErrorMessage}>{error}</Text>
    </Alert>
);

/**
 * The unfinished-card placeholder — an invitation, not a diagnosis.
 *
 * Deliberately NOT an `Alert`: the defect this replaces was a one-second-old card
 * announcing a failure in red. It borrows the design system's empty-state shape
 * (icon in a muted circle, title, hint) so it reads as "there is nothing here YET",
 * which is what every other empty surface in the app says.
 *
 * `reasons` is the builder validator's own field list, so the card names what it is
 * still waiting for and the author can see it WITHOUT opening the panel — which
 * matters while dragging a half-built report around. Capped at three: the tile is
 * often only a few rows tall, and the panel lists them all anyway.
 *
 * A READER can meet one too (an author can leave an unfinished card in a saved
 * report), so the copy splits: an author is told what to do, a reader is told only
 * that it is unset, because they have nothing to do about it.
 */
const WidgetUnconfiguredTile = ({ editMode, reasons }: { editMode: boolean; reasons: string[] }) => (
    <Stack
        align="center"
        justify="center"
        gap={6}
        h="100%"
        p="md"
        data-testid={componentsTestIds.widgetUnconfigured}
    >
        <ThemeIcon variant="light" color="gray" radius="xl" size={36}>
            <IconSettings size={18} />
        </ThemeIcon>
        <Text size="sm" fw={600} c="dimmed" ta="center">
            {editMode ? 'Configurá esta tarjeta' : 'Tarjeta sin configurar'}
        </Text>
        {editMode && reasons.length ? (
            <Text size="xs" c="dimmed" ta="center" data-testid={componentsTestIds.widgetUnconfiguredReasons}>
                Falta: {reasons.slice( 0, 3 ).join( ' · ' )}
            </Text>
        ) : (
            <Text size="xs" c="dimmed" ta="center">
                {editMode
                    ? 'Elegí la fuente de datos y la métrica en el panel de configuración.'
                    : 'Todavía no tiene una fuente de datos.'}
            </Text>
        )}
    </Stack>
);

export const WebWidget = ({ data, widget, type, widgetComponentProps, editMode = false }: WidgetProps ) => {
    // No entry in the data map yet -> genuinely still loading.
    if ( data === undefined || data === null ) {
        return <Skeleton height={'100%'}/>;
    }

    /*
     * UNFINISHED comes before BROKEN, and they are different tiles (2026-08-17).
     *
     * `incomplete` is produced by exactly one thing — `parseWidgetDefinition`
     * refusing a definition that cannot yet describe a query — which is the normal
     * condition of a card for as long as someone is filling it in. It gets an
     * invitation. A genuine failure (an unknown Cube member, a 401, a readmodel
     * HTTP error) still gets the red tile and the message naming the cause.
     *
     * See `isWidgetDataIncomplete` for why this is decided at the SOURCE rather
     * than sniffed from the definition here.
     */
    if ( isWidgetDataIncomplete( data as WidgetDataState )) {
        return (
            <WidgetUnconfiguredTile
                editMode={editMode}
                reasons={( data as { status: 'incomplete'; reasons: string[] }).reasons}
            />
        );
    }

    if ( isWidgetDataError( data as WidgetDataState )) {
        return <WidgetErrorTile error={( data as { status: 'error'; error: string }).error} />;
    }

    // `status: 'ok'` — unwrap. (A bare payload is still accepted so that the many
    // existing component tests, which hand `data` in directly, keep working.)
    const payload = ( data as WidgetDataState )?.status === 'ok'
        ? ( data as { status: 'ok'; data: unknown }).data
        : data;

    const WidgetComponent = widgetComponents[type];
    const parseData = widgetParsers[type];
    const parsedData = parseData ? parseData( payload, widgetComponentProps, widget.definition ) : payload;
    const buildProps = widgetPropsBuilders[type];
    const widgetProps = buildProps
        ? buildProps( parsedData, widgetComponentProps, {
            editMode,
            // Off the DEFINITION, which is where the query lives.
            collection: ( widget.definition as { collection?: string } | undefined )?.collection
        })
        : { data: parsedData };
    return <WidgetComponent {...widgetProps} />;
};
